import type { AxiosError, AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { performLogoutWithFallback } from 'src/utils/logout';

import { setTokenExpiration, isTokenAboutToExpire } from 'src/auth/token-manager';

import { saveAuthToStorage, getAuthFromStorage } from 'src/lib/auth-storage';

/** The API's origin plus `/api`, e.g. `https://server.jobjourney.me/api`. */
export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5015/api';

const axiosInstance = axios.create({
  baseURL: `${API_URL}/v2`,
  headers: {
    Accept: 'application/json',
  },
  timeout: 120000,
});

// Token refresh state
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const refreshToken = async (): Promise<string | null> => {
  try {
    const response = await axiosInstance.post('/auth/refresh-token', {});
    const { token, expiresAt } = response.data?.data ?? {};
    if (token) {
      const auth = getAuthFromStorage();
      if (auth) {
        auth.token = token;
        saveAuthToStorage(auth);
        setTokenExpiration(expiresAt);
      }
      return token;
    }
    return null;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return null;
  }
};

// Response interceptor — handle 401
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      performLogoutWithFallback();
    }
    return Promise.reject(error);
  }
);

// Request interceptor — attach token, proactive refresh
axiosInstance.interceptors.request.use(
  async (config) => {
    const auth = getAuthFromStorage();
    if (auth?.token) {
      if (isTokenAboutToExpire() && !config.url?.includes('/auth/refresh-token')) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshToken().finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
        }
        if (refreshPromise) {
          const newToken = await refreshPromise;
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
            return config;
          }
        }
      }
      config.headers.Authorization = `Bearer ${auth.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

class ApiError extends Error {
  errorCode: string;

  isSuccess: boolean;

  constructor(message: string, errorCode: string) {
    super(message);
    this.name = 'ApiError';
    this.errorCode = errorCode;
    this.isSuccess = false;
  }
}

function handleError(err: unknown): never {
  const error = err as AxiosError;

  // HTML error page from server
  if (
    error.response?.data &&
    typeof error.response.data === 'string' &&
    ((error.response.data as string).includes('<!doctype') ||
      (error.response.data as string).includes('<html'))
  ) {
    throw new ApiError(
      `Server error: ${error.response.status} ${error.response.statusText}`,
      'SERVER_HTML_RESPONSE'
    );
  }

  if (error.response?.data) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw error.response.data;
  }

  throw new ApiError(error.message || 'Request failed', (error as AxiosError).code || 'UNKNOWN_ERROR');
}

export const apiClient = {
  get: async <T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.get<T>(endpoint, config);
      return response.data;
    } catch (err) {
      return handleError(err);
    }
  },

  post: async <T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.post<T>(endpoint, data, config);
      return response.data;
    } catch (err) {
      return handleError(err);
    }
  },

  put: async <T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.put<T>(endpoint, data, config);
      return response.data;
    } catch (err) {
      return handleError(err);
    }
  },

  delete: async <T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.delete<T>(endpoint, config);
      return response.data;
    } catch (err) {
      return handleError(err);
    }
  },

  getBinary: async (endpoint: string): Promise<Blob> => {
    try {
      const response = await axiosInstance.get(endpoint, { responseType: 'blob' });
      return new Blob([response.data], { type: response.headers['content-type'] });
    } catch (err) {
      return handleError(err);
    }
  },

  postBinary: async (endpoint: string, data?: unknown): Promise<Blob> => {
    try {
      const response = await axiosInstance.post(endpoint, data, { responseType: 'blob' });
      return new Blob([response.data], { type: response.headers['content-type'] });
    } catch (err) {
      return handleError(err);
    }
  },
};

type Envelope = { errorCode?: string | null; message?: string; isSuccess: boolean };

/**
 * The payload's fields next to the envelope's, for callers that read both off one
 * object (`response.token`, `response.isSuccess`).
 */
export function flatten<T extends object>(response: Envelope & { data?: T | null }): T & Envelope {
  const { data, ...envelope } = response;
  return { ...(data as T), ...envelope };
}

export { ApiError, axiosInstance };
